import { useState, useRef } from "react";
import { useMutation } from "@apollo/client";
import { FiImage, FiUpload, FiEdit, FiFile } from "react-icons/fi";
import Button from "components/base/Button";
import { Alert } from "components/base/Toast";
import { Select, Switcher } from "components/base/Form";
import useUploadProtection from "hooks/useUploadProtection";
import { CREATE_LOGO, UPDATE_LOGO } from "./graphql";

const LogoFormModal = ({ logo = null, refetch, hide }) => {
  const isEdit = !!logo;
  const [createLogo] = useMutation(CREATE_LOGO);
  const [updateLogo] = useMutation(UPDATE_LOGO);
  const originFileInputRef = useRef(null);

  const [formData, setFormData] = useState({
    name: logo?.name || "",
    category: logo?.category || "product",
    active: logo?.active ?? true,
  });

  const [imageFile, setImageFile] = useState(null);
  const [originFile, setOriginFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(logo?.url || null);
  const [submitting, setSubmitting] = useState(false);

  useUploadProtection(
    submitting,
    "Logo upload is in progress. Leaving now will cancel the upload. Continue?",
  );

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setImageFile(file);
    const reader = new FileReader();
    reader.onloadend = () => setImagePreview(reader.result);
    reader.readAsDataURL(file);
  };

  const handleOriginFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setOriginFile(file);
  };

  const handleSubmit = async () => {
    if (!formData.name) {
      Alert("error", "Logo name is required");
      return;
    }

    if (!isEdit && (!imageFile || !originFile)) {
      Alert("error", "Please fill in all required fields");
      return;
    }

    try {
      setSubmitting(true);

      if (isEdit) {
        const updateVariables = { id: logo.id };

        // Only send changed fields
        if (formData.name !== logo.name) {
          updateVariables.name = formData.name;
        }
        if (formData.category !== logo.category) {
          updateVariables.category = formData.category;
        }
        if (formData.active !== logo.active) {
          updateVariables.active = formData.active;
        }
        if (imageFile) {
          updateVariables.image = imageFile;
        }
        if (originFile) {
          updateVariables.originFile = originFile;
        }

        await updateLogo({ variables: updateVariables });
        Alert("success", "Logo updated successfully");
      } else {
        await createLogo({
          variables: {
            name: formData.name,
            image: imageFile,
            category: formData.category,
            originFile: originFile,
          },
        });
        Alert("success", "Logo created successfully");
      }

      refetch();
      hide();
      setSubmitting(false);
    } catch (error) {
      Alert("error", error.message);
      setSubmitting(false);
    }
  };

  return (
    <div className="flex flex-col max-h-[80vh]">
      {/* Title */}
      <div className="flex items-center space-x-3 px-1 pb-4">
        <FiEdit className="text-blue-500 text-2xl" />
        <h2 className="text-2xl font-bold">
          {isEdit ? "Edit Logo" : "Create New Logo"}
        </h2>
      </div>

      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto p-2 space-y-8">
        {/* Image Preview with Click to Upload */}
        <div className="relative overflow-hidden rounded-xl shadow-lg group">
          {imagePreview ? (
            <>
              <img
                src={imagePreview}
                alt="Logo preview"
                className="w-full h-96 object-contain bg-gray-50 dark:bg-gray-800"
              />
              <label className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity duration-300 cursor-pointer flex items-center justify-center">
                <div className="text-center text-white">
                  <FiUpload className="mx-auto text-5xl mb-3" />
                  <p className="text-lg font-semibold">Click to change image</p>
                  <p className="text-sm mt-1">PNG, JPG up to 10MB</p>
                </div>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                  className="hidden"
                />
              </label>
            </>
          ) : (
            <label className="block border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-xl h-96 cursor-pointer hover:border-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-all">
              <div className="flex flex-col items-center justify-center h-full text-gray-500">
                <FiImage className="text-6xl mb-4" />
                <p className="text-lg font-semibold text-gray-700 dark:text-gray-300">
                  Upload Logo Image{" "}
                  {!isEdit && <span className="text-red-500">*</span>}
                </p>
                <p className="text-sm mt-2">PNG, JPG up to 10MB</p>
              </div>
              <input
                type="file"
                accept="image/*"
                onChange={handleImageChange}
                className="hidden"
              />
            </label>
          )}
        </div>

        {/* Form Fields */}
        <div className="space-y-8">
          {/* Name */}
          <div>
            <label className="block text-sm font-medium mb-2">
              Logo Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) =>
                setFormData({ ...formData, name: e.target.value })
              }
              className="w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Enter logo name"
            />
          </div>

          {/* Origin File */}
          <div>
            <label className="block text-sm font-medium mb-2">
              Origin File {!isEdit && <span className="text-red-500">*</span>}
            </label>
            <div className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
              <FiFile className="text-gray-400 text-xl flex-shrink-0" />

              <div className="flex-1 min-w-0">
                {isEdit && logo.originFile && !originFile ? (
                  <a
                    href={logo.originFile}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-blue-600 hover:text-blue-700 truncate block"
                    title={logo.originFile}
                  >
                    {logo.originFile.split("/").pop()}
                  </a>
                ) : originFile ? (
                  <span className="text-sm text-gray-700 dark:text-gray-300 truncate block">
                    {originFile.name}
                  </span>
                ) : (
                  <span className="text-sm text-gray-400">
                    No file selected
                  </span>
                )}
              </div>

              <input
                ref={originFileInputRef}
                type="file"
                accept=".zip,.rar,.7z"
                onChange={handleOriginFileChange}
                className="hidden"
              />
              <Button
                onClick={() => originFileInputRef.current?.click()}
                className="flex items-center"
                icon={<FiUpload />}
              >
                {originFile ? "Change" : isEdit ? "Replace" : "Upload"}
              </Button>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Upload zip file with design sources
            </p>
          </div>

          {/* Category and Active */}
          <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg py-2">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="flex items-center gap-2">
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300 whitespace-nowrap">
                  Category:
                </label>
                <Select
                  value={formData.category}
                  onChange={(e) =>
                    setFormData({ ...formData, category: e.target.value })
                  }
                >
                  <option value="product">Product</option>
                  <option value="brand">Brand</option>
                </Select>
              </div>

              <div className="flex items-center gap-2">
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300 whitespace-nowrap">
                  Active:
                </label>
                <div className="flex items-center gap-2 flex-1">
                  <Switcher
                    isOn={formData.active}
                    onChange={() =>
                      setFormData({ ...formData, active: !formData.active })
                    }
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="flex space-x-3 pt-4 px-1 border-t dark:border-gray-700">
        <Button
          primary
          loading={submitting}
          disabled={submitting}
          onClick={handleSubmit}
          className="flex-1 justify-center"
        >
          {isEdit ? "Update Logo" : "Create Logo"}
        </Button>
      </div>
    </div>
  );
};

export default LogoFormModal;
